import React, { createContext, useContext } from 'react';
import { useRecoilValue } from 'recoil';

import { useBatchTicketActions } from '../../../store/batchTicket/actions';
import { basketAdvisorOrderIdState, productsCartState } from '../../../store/batchTicket/atom';
import { AssetSelectedTypeMap } from '../../../store/product/types';
import { ProductsType } from '../components/DynamicAsset/constants';

export type BatchTicketAssetsSelection = {
  [K in ProductsType]: { items: AssetSelectedTypeMap[K][] };
};

interface BatchTicketAssetsContextReceivedData {}

interface BatchTicketAssetsContextData extends BatchTicketAssetsContextReceivedData {
  selectedProducts: BatchTicketAssetsSelection;
  isProductSelected: <T extends ProductsType>(
    productType: T,
    product: AssetSelectedTypeMap[T],
    idSelector: keyof AssetSelectedTypeMap[T]
  ) => boolean;
  selectProduct: <T extends ProductsType>(productType: T, product: AssetSelectedTypeMap[T]) => void;
  clearProduct: () => void;
  deselectProduct: <T extends ProductsType>(
    productType: T,
    product: AssetSelectedTypeMap[T],
    idSelector: keyof AssetSelectedTypeMap[T]
  ) => void;
  loadingProducts: BatchTicketAssetsSelection;
  setLoadingProduct: <T extends ProductsType>(
    productType: T,
    product: AssetSelectedTypeMap[T]
  ) => void;
  resetLoadingProduct: <T extends ProductsType>(
    productType: T,
    product: AssetSelectedTypeMap[T],
    idSelector: keyof AssetSelectedTypeMap[T]
  ) => void;
  isProductLoading: <T extends ProductsType>(
    productType: T,
    product: AssetSelectedTypeMap[T],
    idSelector: keyof AssetSelectedTypeMap[T]
  ) => boolean;
  basketAdvisorOrderId: string | null;
}

interface Props extends BatchTicketAssetsContextReceivedData {
  children: React.ReactNode;
}

const BatchTicketAssetsContext = createContext<BatchTicketAssetsContextData>(
  {} as BatchTicketAssetsContextData
);

const BatchTicketAssetsProvider: React.FC<Props> = ({ children, ...props }) => {
  const productsCart = useRecoilValue(productsCartState);
  const basketAdvisorOrderId = useRecoilValue(basketAdvisorOrderIdState);
  const { addProductToCart, removeProductToCart, cleanProductCart } = useBatchTicketActions();

  const [loadingProducts, setLoadingProducts] = React.useState<BatchTicketAssetsSelection>({
    fixedIncome: { items: [] },
    coes: { items: [] },
    directTreasury: { items: [] },
    equity: { items: [] },
    funds: { items: [] },
  });

  const selectProduct: BatchTicketAssetsContextData['selectProduct'] = (productType, product) => {
    addProductToCart(productType, product);
  };

  const deselectProduct: BatchTicketAssetsContextData['deselectProduct'] = (
    productType,
    product,
    idSelector
  ) => {
    removeProductToCart(productType, product, idSelector);
  };

  const clearProduct = () => cleanProductCart(basketAdvisorOrderId!);

  const isProductSelected: BatchTicketAssetsContextData['isProductSelected'] = (
    productType,
    product,
    idSelector
  ) => {
    return productsCart[productType].items.some((item) => item[idSelector] === product[idSelector]);
  };

  const setLoadingProduct: BatchTicketAssetsContextData['setLoadingProduct'] = (
    productType,
    product
  ) => {
    setLoadingProducts((prevState) => {
      return {
        ...prevState,
        [productType]: {
          items: [...prevState[productType].items, product],
        },
      };
    });
  };

  const resetLoadingProduct: BatchTicketAssetsContextData['resetLoadingProduct'] = (
    productType,
    product,
    idSelector
  ) => {
    setLoadingProducts((prevState) => {
      return {
        ...prevState,
        [productType]: {
          items: prevState[productType].items.filter(
            (item) => item[idSelector] !== product[idSelector]
          ),
        },
      };
    });
  };

  const isProductLoading: BatchTicketAssetsContextData['isProductLoading'] = (
    productType,
    product,
    idSelector
  ) => {
    return loadingProducts[productType].items.some(
      (item) => item[idSelector] === product[idSelector]
    );
  };

  return (
    <BatchTicketAssetsContext.Provider
      value={{
        ...props,
        selectedProducts: productsCart,
        loadingProducts,
        isProductSelected,
        selectProduct,
        deselectProduct,
        clearProduct,
        setLoadingProduct,
        resetLoadingProduct,
        isProductLoading,
        basketAdvisorOrderId,
      }}>
      {children}
    </BatchTicketAssetsContext.Provider>
  );
};

const useBatchTicketAssetsContext = (): BatchTicketAssetsContextData => {
  const context = useContext(BatchTicketAssetsContext);

  if (!context) {
    throw new Error('useBatchTicketAssetsContext must be used within an BatchTicketAssetsContext');
  }
  return context;
};

export { BatchTicketAssetsProvider, useBatchTicketAssetsContext, BatchTicketAssetsContextData };
